import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { FiTrash2 } from "react-icons/fi";
import { ReactComponent as BriefcaseIcon } from "../../assets/icons/briefcase-02.svg";
import { imageCache } from "../../utils/imageCache";
import type { UserProject } from "../../services/projectService";

export type { UserProject };

interface ProjectCardProps {
  project: UserProject;
  onDelete: (id: string, title: string) => void;
}

const ProjectCard: React.FC<ProjectCardProps> = ({ project, onDelete }) => {
  const navigate = useNavigate();
  const [signedImageUrl, setSignedImageUrl] = useState<string | null>(null);

  useEffect(() => {
    let active = true;
    const fetchSignedUrl = async () => {
      if (project.imageUrl) {
        try {
          const url = await imageCache.getSignedUrl(project.imageUrl);
          if (active && url) setSignedImageUrl(url);
        } catch (err) {
          console.error('[ProjectCard] Error fetching signed image URL:', err);
        }
      } else {
        setSignedImageUrl(null);
      }
    };
    fetchSignedUrl();
    return () => { active = false; };
  }, [project.imageUrl]);

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={() => navigate(`/home/projects/${project.id}`)}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          navigate(`/home/projects/${project.id}`);
        }
      }}
      className="border border-gray-200 rounded-md overflow-hidden hover:border-gray-500 transition-colors flex flex-col cursor-pointer focus:outline-none focus-visible:ring-2 focus-visible:ring-eai-primary-accent"
    >
      {/* Image / placeholder */}
      <div className="h-28 bg-eai-checklist-lt flex items-center justify-center overflow-hidden flex-shrink-0">
        {signedImageUrl ? (
          <img
            src={signedImageUrl}
            alt={project.title}
            className="w-full h-full object-cover"
          />
        ) : (
          <BriefcaseIcon className="w-10 h-10 stroke-eai-primary-accent opacity-40" />
        )}
      </div>

      <div className="p-5 flex flex-col gap-3 flex-1">
        <div className="flex items-start justify-between gap-2">
          <div className="text-gray-900 text-base font-semibold font-Inter leading-snug">
            {project.title}
          </div>
          <button
            onClick={(e) => { e.stopPropagation(); onDelete(project.id, project.title); }}
            className="shrink-0 text-gray-300 hover:text-red-400 transition-colors p-1 rounded"
            title="Delete project"
          >
            <FiTrash2 size={15} />
          </button>
        </div>

        <p className="text-gray-500 text-sm font-normal font-Inter line-clamp-2 leading-relaxed">
          {project.description}
        </p>

      </div>
    </div>
  );
};

export default ProjectCard;
